/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {useState} from 'react';
import {GenericObject} from '@ion/core/src/types/entities';

export interface FormState {
  [key: string]: {
    name?: string;
    value?: any;
    changed?: boolean;
    required?: boolean;
    error?: boolean;
    errorMessage?: string;
    isShadow?: boolean;
  }
}

// allows for overriding the form state values with other types (intended for editing entities,
// such that the current state of the entity should be included in the form state)
export interface AugmentedFormState {
  [key: string]: {
    name: string;
    value: any;
    required?: boolean;
    error?: boolean;
    isShadow?: boolean;
  } | any
}

/**
 * @useFormState
 *
 * Simple hook for managing the state of a form, which
 * includes am optional argument for clearing the object. This
 * hook is specific to an object that has name, value,
 * required, and error properties for each root property.
 *
 * ```typescript
 *
 *   import {useFormState} from '@ion/dls/src/hooks/useFormState';
 *
 *   const [formState, setFormState] = useFormState({
 *     name: {
 *       required: true,
 *       value: 'Truck123'
 *     }
 *   });
 *
 *  ...in component
 *
 *  setFormState({
 *    assetType: {
 *      required: false,
 *      value: 'Vehicle'
 *    }
 *  }) // {name: {...}, assetType{...}}
 *
 *  setFormState({
 *    assetType: {
 *      value: 'Trailer'
 *    }
 *  }) // {name: {...}, assetType{
 *    required: false,
 *    value: 'Trailer'
 *  }}
 *
 *  setFormState({}, clear) // {name: {...}}
 *
 * ```
 *
 */
export const useFormState = (
  initialState: FormState | {} = {}
): [GenericObject, (val: FormState, clear?: boolean) => void] => {
  const [formValues, setFormValues] = useState(initialState);

  const setFormState = (val: FormState, clear?: boolean) => {
    setFormValues((previousFormState) => (clear ? {...initialState} : {...previousFormState, ...val}));
  };
  return [formValues, setFormState];
};

/**
 * @validate
 *
 * Validates input fields
 * Takes as input a value to validate and a function to use as a validator.
 */
export const validate = (val: any, validator: (val: any, compareVal?: any) => boolean,
  compareVal?: any): boolean => {
  return validator(val?.value || val, compareVal?.value || compareVal);
};

export default useFormState;
